import NFERecebidas from './NFERecebidas';
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Row, Col, DatePicker, Statistic, Tabs, Input, Modal, InputNumber, Select, Form } from 'antd';
import { SyncOutlined, FilePdfOutlined, DollarOutlined, FileTextOutlined, FileProtectOutlined, DownloadOutlined, StopOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import dayjs from 'dayjs';
const { Option } = Select;

const fmtMoeda = (v) => v ? 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-';
const fmtData = (v) => v ? dayjs(v).format('DD/MM/YYYY') : '-';
const STATUS_COR = { emitida: 'green', cancelada: 'red', erro: 'orange' };
const STATUS_LABEL = { emitida: 'Emitida', cancelada: 'Cancelada', erro: 'Erro' };

function NFSeEmitidas() {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroCompetencia, setFiltroCompetencia] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const navigate = useNavigate();
  const [sincronizando, setSincronizando] = useState(false);

  useEffect(() => { carregar(); }, [filtroCompetencia, filtroPeriodo, filtroStatus]);

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/nfse');
      let dados = r.data;
      if (filtroStatus) dados = dados.filter(n => n.status === filtroStatus);
      if (filtroCompetencia) dados = dados.filter(n => n.competencia_mes?.startsWith(filtroCompetencia.format('YYYY-MM')));
      if (filtroPeriodo?.[0]) dados = dados.filter(n => dayjs(n.criado_em).isAfter(filtroPeriodo[0]) && dayjs(n.criado_em).isBefore(filtroPeriodo[1]));
      setNotas(dados);
    } catch { message.error('Erro ao carregar notas'); }
    finally { setLoading(false); }
  };

  const baixarXML = async (id, numero) => {
    try {
      const r = await api.get(`/nfse/${id}/xml`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe-${numero}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { message.error('Erro ao baixar XML'); }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.post('/nfse/importar-historico');
      message.success('Sincronizado! ' + (r.data.importadas || 0) + ' notas novas.');
      carregar();
    } catch { message.error('Erro ao sincronizar'); }
    finally { setSincronizando(false); }
  };

  const abrirPDF = async (id, numero, temProposta) => {
    try {
      if (temProposta) {
        const r = await api.get(`/nfse/${id}/pdf-url`);
        window.open(r.data.url, '_blank');
      } else {
        const r = await api.get(`/nfse/${id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `NFSe-${numero}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch { message.error('Erro ao abrir PDF'); }
  };

  const totalValor = notas.filter(n => n.status === 'emitida').reduce((s, n) => s + parseFloat(n.valor_servico || 0), 0);

  const colunas = [
    { title: 'NFS-e', dataIndex: 'numero_nfse', width: 90, render: (v, r) => <Tag color="blue">{v || '#' + r.numero}</Tag> },
    { title: 'Cliente', key: 'cliente', ellipsis: true, width: 200, render: (_, r) => r.razao_social || r.nome_cliente || '-' },
    { title: 'Compet.', dataIndex: 'competencia_mes', width: 80, render: v => v ? v.substring(5) + '/' + v.substring(0, 4) : '-' },
    { title: 'Emitida em', key: 'data', width: 100, render: (_, r) => fmtData(r.data_emissao || r.criado_em) },
    { title: 'Valor', dataIndex: 'valor_servico', width: 110, render: v => fmtMoeda(v) },
    { title: 'Status', dataIndex: 'status', width: 90, render: v => <Tag color={STATUS_COR[v]}>{STATUS_LABEL[v] || v}</Tag> },
    { title: 'Ações', width: 130, render: (_, r) => (
      <Space size={4}>
        {r.proposta_id && <Button size="small" onClick={() => navigate('/propostas/' + r.proposta_id)}>Proposta</Button>}
        {r.status === 'emitida' && <Button size="small" icon={<FilePdfOutlined />} onClick={() => abrirPDF(r.id, r.numero_nfse, r.proposta_id)} />}
        {r.status === 'emitida' && <Button size="small" onClick={() => baixarXML(r.id, r.numero_nfse)}>XML</Button>}
      </Space>
    )},
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="Total Emitidas" value={notas.filter(n=>n.status==='emitida').length} prefix={<FileTextOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Valor Total" value={fmtMoeda(totalValor)} prefix={<DollarOutlined style={{ color: '#52c41a' }} />} /></Card></Col>
      </Row>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button icon={<SyncOutlined />} onClick={sincronizar} loading={sincronizando}>Sincronizar com Sistema Nacional</Button>
      </div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16}>
          <Col span={8}><DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder={['Data início', 'Data fim']} onChange={v => setFiltroPeriodo(v)} /></Col>
          <Col span={6}><DatePicker picker="month" placeholder="Competência" style={{ width: '100%' }} format="MM/YYYY" onChange={v => setFiltroCompetencia(v)} /></Col>
          <Col span={6}><select style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #d9d9d9' }} onChange={e => setFiltroStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="emitida">Emitida</option>
            <option value="cancelada">Cancelada</option>
            <option value="erro">Erro</option>
          </select></Col>
        </Row>
      </Card>
      <Card size="small">
        <Table columns={colunas} dataSource={notas} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }} locale={{ emptyText: 'Nenhuma nota encontrada.' }} />
      </Card>
    </div>
  );
}

function NFSeRecebidas() {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importando, setImportando] = useState(false);
  const [filtroCompetencia, setFiltroCompetencia] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState(null);

  useEffect(() => { carregar(); }, [filtroCompetencia, filtroPeriodo]);

  const carregar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroCompetencia) params.competencia = filtroCompetencia.format('YYYY-MM');
      if (filtroPeriodo?.[0]) { params.periodo_inicio = filtroPeriodo[0].format('YYYY-MM-DD'); params.periodo_fim = filtroPeriodo[1].format('YYYY-MM-DD'); }
      const r = await api.get('/nfse-recebidas', { params });
      setNotas(r.data);
    } catch { message.error('Erro ao carregar notas recebidas'); }
    finally { setLoading(false); }
  };

  const importar = async () => {
    setImportando(true);
    try {
      const r = await api.post('/nfse-recebidas/importar');
      message.success(`${r.data.importadas} novas notas importadas!`);
      carregar();
    } catch { message.error('Erro ao importar'); }
    finally { setImportando(false); }
  };

  const baixarXML = async (id, numero) => {
    try {
      const r = await api.get(`/nfse-recebidas/${id}/xml`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe-${numero}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { message.error('Erro ao baixar XML'); }
  };

  const baixarPDF = async (id, numero) => {
    try {
      const r = await api.get(`/nfse-recebidas/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe-recebida-${numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { message.error('Erro ao baixar PDF'); }
  };

  const totalValor = notas.reduce((s, n) => s + parseFloat(n.valor_servico || 0), 0);
  const totalISS = notas.reduce((s, n) => s + parseFloat(n.valor_iss || 0), 0);

  const colunas = [
    { title: 'NFS-e', dataIndex: 'numero_nfse', width: 100, render: v => <Tag color="purple">{v || '-'}</Tag> },
    { title: 'Prestador', key: 'prestador', render: (_, r) => <><div><strong>{r.razao_prestador}</strong></div><div style={{ fontSize: 11, color: '#888' }}>{r.cnpj_prestador}</div></> },
    { title: 'Competência', dataIndex: 'competencia_mes', width: 110, render: v => v ? v.substring(5) + '/' + v.substring(0, 4) : '-' },
    { title: 'Data Emissão', dataIndex: 'data_emissao', width: 120, render: v => fmtData(v) },
    { title: 'Valor', dataIndex: 'valor_servico', width: 120, render: v => fmtMoeda(v) },
    { title: 'ISS', dataIndex: 'valor_iss', width: 100, render: v => fmtMoeda(v) },
    { title: 'Status', dataIndex: 'status', width: 90, render: v => <Tag color={v === 'ativa' ? 'green' : 'red'}>{v}</Tag> },
    { title: 'Ações', width: 140, render: (_, r) => <Space><Button size="small" icon={<FilePdfOutlined />} onClick={() => baixarPDF(r.id, r.numero_nfse)}>PDF</Button><Button size="small" onClick={() => baixarXML(r.id, r.numero_nfse)}>XML</Button></Space> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<SyncOutlined />} onClick={importar} loading={importando}>Sincronizar com Sistema Nacional</Button>
      </div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="Total Recebidas" value={notas.length} prefix={<FileTextOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Valor Total" value={fmtMoeda(totalValor)} prefix={<DollarOutlined style={{ color: '#722ed1' }} />} /></Card></Col>
        <Col span={6}><Card><Statistic title="ISS Total" value={fmtMoeda(totalISS)} prefix={<DollarOutlined style={{ color: '#fa8c16' }} />} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16}>
          <Col span={8}><DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder={['Data início', 'Data fim']} onChange={v => setFiltroPeriodo(v)} /></Col>
          <Col span={6}><DatePicker picker="month" placeholder="Competência" style={{ width: '100%' }} format="MM/YYYY" onChange={v => setFiltroCompetencia(v)} /></Col>
          <Col span={4}><Tag color="purple">{notas.length} notas</Tag></Col>
        </Row>
      </Card>
      <Card size="small">
        <Table columns={colunas} dataSource={notas} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }} locale={{ emptyText: 'Nenhuma nota encontrada. Clique em Sincronizar.' }} />
      </Card>
    </div>
  );
}

function NFEEmitidas() {
  const [nfes, setNfes] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busca, setBusca] = React.useState('');
  const [periodo, setPeriodo] = React.useState([null, null]);

  const carregar = () => {
    setLoading(true);
    let url = '/nfe';
    if (periodo[0] && periodo[1]) url += `?inicio=${periodo[0].format('YYYY-MM-DD')}&fim=${periodo[1].format('YYYY-MM-DD')}`;
    api.get(url).then(r => setNfes(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  React.useEffect(() => { carregar(); }, []);

  const [modalComplementar, setModalComplementar] = React.useState(false);
  const [nfeSelecionada, setNfeSelecionada] = React.useState(null);
  const [itensComplementar, setItensComplementar] = React.useState([]);
  const [infAdicComplementar, setInfoAdicComplementar] = React.useState('');
  const [emitindoComplementar, setEmitindoComplementar] = React.useState(false);
  const [produtosComplementar, setProdutosComplementar] = React.useState([]);

  const abrirComplementar = async (nfe) => {
    setNfeSelecionada(nfe);
    setItensComplementar([{ produto_id: null, descricao: '', quantidade: 1, unidade: 'UN', preco_unitario: 0, ncm: '', origem: 0, avulso: false }]);
    const rp = await api.get('/produtos');
    setProdutosComplementar(rp.data || []);
    setInfoAdicComplementar('Nota Complementar referente NF-e ' + nfe.chave_acesso);
    setModalComplementar(true);
  };

  const emitirComplementar = async () => {
    setEmitindoComplementar(true);
    try {
      await api.post('/nfe/' + nfeSelecionada.id + '/complementar', { itens: itensComplementar, infAdic: infAdicComplementar });
      message.success('NF-e Complementar emitida!');
      setModalComplementar(false);
      carregar();
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
    finally { setEmitindoComplementar(false); }
  };

  const cancelar = async (nfe) => {
    const motivo = prompt('Motivo do cancelamento (mínimo 15 caracteres):');
    if (!motivo || motivo.length < 15) { message.warning('Motivo deve ter pelo menos 15 caracteres'); return; }
    try {
      await api.post('/nfe/' + nfe.id + '/cancelar', { motivo });
      message.success('NF-e cancelada!');
      carregar();
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
  };

  const nfesFiltradas = nfes.filter(n =>
    !busca ||
    (n.razao_social || '').toLowerCase().includes(busca.toLowerCase()) ||
    (n.chave_acesso || '').includes(busca) ||
    (n.proposta_numero || '').includes(busca) ||
    String(n.numero || '').includes(busca)
  );

  return (
    <>
    <Card size="small">
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col span={8}><Input.Search placeholder="Buscar por cliente, chave, número..." value={busca} onChange={e => setBusca(e.target.value)} allowClear /></Col>
        <Col span={8}><DatePicker.RangePicker format="DD/MM/YYYY" onChange={v => setPeriodo(v || [null, null])} style={{ width: '100%' }} /></Col>
        <Col span={4}><Button type="primary" onClick={carregar}>Filtrar</Button></Col>
      </Row>
      <Table dataSource={nfesFiltradas} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }}
        columns={[
          { title: 'Nº', dataIndex: 'numero', width: 60, render: v => <Tag color="blue">{v}</Tag> },
          { title: 'Ações', width: 180, render: (_, r) => (
            <Space size={4}>
              <Button size="small" icon={<FilePdfOutlined />} title="Baixar DANFE" onClick={async () => { try { const res = await api.get('/nfe/' + r.id + '/danfe', { responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); window.open(url, '_blank'); } catch { message.error('Erro ao gerar DANFE'); } }} />
              <Button size="small" icon={<DownloadOutlined />} title="Baixar XML" onClick={async () => {
                try {
                  const res = await api.get('/nfe/' + r.id + '/xml', { responseType: 'blob' });
                  const url = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a'); a.href = url; a.download = 'NFe-' + r.chave_acesso + '.xml'; a.click();
                } catch { message.error('Erro ao baixar XML'); }
              }} />
              {r.status === 'autorizada' && <Button size="small" icon={<PlusOutlined />} title="NF-e Complementar" onClick={() => abrirComplementar(r)} />}
              {r.status === 'autorizada' && <Button size="small" icon={<EditOutlined />} title="Carta de Correção" onClick={async () => {
                const correcao = prompt('Descrição da correção (mínimo 15 caracteres):');
                if (!correcao || correcao.length < 15) { message.warning('Correção deve ter pelo menos 15 caracteres'); return; }
                try {
                  await api.post('/nfe/' + r.id + '/carta-correcao', { xCorrecao: correcao });
                  message.success('Carta de Correção enviada!');
                } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
              }} />}
              {r.status === 'autorizada' && <Button size="small" danger icon={<StopOutlined />} title="Cancelar" onClick={() => cancelar(r)} />}
              {r.status === 'cancelada' && <Button size="small" icon={<FileProtectOutlined />} title="Emitir NF-e Substituta" onClick={async () => {
                try { await api.post('/nfe/' + r.id + '/substituir'); message.success('NF-e substituta emitida!'); carregar(); }
                catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
              }} />}
            </Space>
          )},
          { title: 'Cliente', dataIndex: 'razao_social', ellipsis: true },
          { title: 'Proposta', dataIndex: 'proposta_numero', width: 110 },
          { title: 'Chave de Acesso', dataIndex: 'chave_acesso', ellipsis: true, width: 180, render: v => <span style={{ fontSize: 11 }}>{v}</span> },
          { title: 'Status', dataIndex: 'status', width: 100, render: v => <Tag color={v==='autorizada'?'green':v==='cancelada'?'red':'orange'}>{v}</Tag> },
          { title: 'Valor', dataIndex: 'valor_total', width: 110, render: v => v ? 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-' },
          { title: 'Emitida em', dataIndex: 'criado_em', width: 130, render: v => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
        ]}
      />
    </Card>
    <>
    <Modal title="NF-e Complementar" open={modalComplementar} onCancel={() => setModalComplementar(false)}
        onOk={emitirComplementar} okText="Emitir Complementar" confirmLoading={emitindoComplementar} width={700}>
      <p style={{ marginBottom: 12 }}>Itens a complementar na NF-e <strong>{nfeSelecionada?.numero}</strong>:</p>
      {itensComplementar.map((it, idx) => (
        <div key={idx} style={{ padding: 8, background: '#f9f9f9', borderRadius: 6, marginBottom: 8 }}>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col span={20}>
              <label style={{ fontSize: 11, color: '#888' }}>Produto</label>
              {it.avulso ? (
                <Input placeholder="Descrição do item avulso" value={it.descricao} onChange={e => { const n=[...itensComplementar]; n[idx].descricao=e.target.value; setItensComplementar(n); }} />
              ) : (
              <Select showSearch optionFilterProp="children" style={{ width: '100%' }} placeholder="Selecione o produto" value={it.produto_id} allowClear
                onChange={v => { const n=[...itensComplementar]; const prod = produtosComplementar.find(p=>p.id===v); if(prod){ n[idx].produto_id=v; n[idx].descricao=prod.descricao; n[idx].unidade=(prod.unidade||'UN').substring(0,6); n[idx].preco_unitario=prod.preco_venda||0; n[idx].ncm=(prod.ncm||'').replace(/\D/g,''); n[idx].origem=prod.origem||0; } setItensComplementar(n); }}>
                {produtosComplementar.map(p => <Option key={p.id} value={p.id}>{p.descricao} {p.ncm ? '(NCM: '+p.ncm+')' : ''}</Option>)}
              </Select>
              )}
            </Col>
            <Col span={4} style={{ paddingTop: 18 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="small" block onClick={() => { const n=[...itensComplementar]; n[idx].avulso=!n[idx].avulso; n[idx].produto_id=null; n[idx].descricao=''; n[idx].ncm=''; setItensComplementar(n); }}>{it.avulso ? 'Usar cadastro' : 'Item avulso'}</Button>
                <Button danger size="small" block onClick={() => setItensComplementar(itensComplementar.filter((_,i)=>i!==idx))}>Remover</Button>
              </Space>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={6}><label style={{ fontSize: 11, color: '#888' }}>Quantidade</label><InputNumber style={{width:'100%'}} min={0} value={it.quantidade} onChange={v => { const n=[...itensComplementar]; n[idx].quantidade=v; setItensComplementar(n); }} /></Col>
            <Col span={6}><label style={{ fontSize: 11, color: '#888' }}>Unidade</label><Input value={it.unidade} onChange={e => { const n=[...itensComplementar]; n[idx].unidade=e.target.value; setItensComplementar(n); }} /></Col>
            <Col span={6}><label style={{ fontSize: 11, color: '#888' }}>Valor unitário (R$)</label><InputNumber style={{width:'100%'}} min={0} precision={2} decimalSeparator="," value={it.preco_unitario} onChange={v => { const n=[...itensComplementar]; n[idx].preco_unitario=v; setItensComplementar(n); }} /></Col>
            <Col span={6}><label style={{ fontSize: 11, color: '#888' }}>NCM</label><Input value={it.ncm} onChange={e => { const n=[...itensComplementar]; n[idx].ncm=e.target.value; setItensComplementar(n); }} /></Col>
          </Row>
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => setItensComplementar([...itensComplementar, { descricao: '', quantidade: 1, unidade: 'UN', preco_unitario: 0 }])} style={{ marginBottom: 12 }}>Adicionar Item</Button>
      <div>
        <label style={{ fontSize: 12, color: '#888' }}>Informações Adicionais</label>
        <Input.TextArea rows={2} value={infAdicComplementar} onChange={e => setInfoAdicComplementar(e.target.value)} />
      </div>
    </Modal>
    </>
    </>
  );
}

function NFERemessa() {
  const [remessas, setRemessas] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modal, setModal] = React.useState(false);
  const [clientes, setClientes] = React.useState([]);
  const [cnpjs, setCnpjs] = React.useState([]);
  const [contatos, setContatos] = React.useState([]);
  const [emitindo, setEmitindo] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    carregar();
    api.get('/clientes').then(r => setClientes(r.data || [])).catch(() => {});
  }, []);

  const carregar = () => {
    setLoading(true);
    api.get('/nfe-remessa').then(r => setRemessas(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  const emitir = async (values) => {
    setEmitindo(true);
    try {
      await api.post('/nfe-remessa', values);
      message.success('NF-e de ' + (values.tipo === 'entrada' ? 'Remessa' : 'Devolução') + ' emitida!');
      setModal(false);
      form.resetFields();
      carregar();
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
    finally { setEmitindo(false); }
  };

  return (
    <>
    <Card size="small" style={{ marginBottom: 8 }} extra={
      <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModal(true); }}>Nova Remessa/Devolução</Button>
    }>
      <Table dataSource={remessas} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }}
        columns={[
          { title: 'Nº', dataIndex: 'numero', width: 70, render: v => <Tag color="blue">{v}</Tag> },
          { title: 'Ações', width: 100, render: (_, r) => (
            <Space size={4}>
              <Button size="small" icon={<FilePdfOutlined />} title="DANFE" onClick={async () => {
                try { const res = await api.get('/nfe-remessa/' + r.id + '/danfe', { responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); window.open(url, '_blank'); }
                catch { message.error('Erro ao gerar DANFE'); }
              }} />
              <Button size="small" icon={<DownloadOutlined />} title="XML" onClick={async () => {
                try { const res = await api.get('/nfe-remessa/' + r.id + '/xml', { responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = 'NFe-remessa-' + r.chave_acesso + '.xml'; a.click(); }
                catch { message.error('Erro ao baixar XML'); }
              }} />
            </Space>
          )},
          { title: 'Tipo', dataIndex: 'tipo', width: 100, render: v => <Tag color={v==='entrada'?'orange':'green'}>{v === 'entrada' ? 'Remessa' : 'Devolução'}</Tag> },
          { title: 'Cliente', dataIndex: 'razao_social', ellipsis: true },
          { title: 'Equipamento', dataIndex: 'descricao_equipamento', ellipsis: true },
          { title: 'Nº Série', dataIndex: 'numero_serie', width: 120 },
          { title: 'CFOP', dataIndex: 'cfop', width: 70 },
          { title: 'Status', dataIndex: 'status', width: 100, render: v => <Tag color={v==='autorizada'?'green':'red'}>{v}</Tag> },
          { title: 'Data', dataIndex: 'criado_em', width: 110, render: v => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
        ]}
      />
    </Card>
    <Modal title="Emitir NF-e de Remessa/Devolução" open={modal} onCancel={() => setModal(false)} footer={null} width={600}>
      <Form form={form} layout="vertical" onFinish={emitir}>
        <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]} initialValue="entrada">
          <Select>
            <Option value="entrada">Remessa para Conserto (cliente envia para você) — CFOP 1915</Option>
            <Option value="saida">Devolução ao Cliente (você devolve) — CFOP 5916</Option>
          </Select>
        </Form.Item>
        <Form.Item name="cliente_id" label="Cliente" rules={[{ required: true }]}>
          <Select showSearch optionFilterProp="children" onChange={async v => {
            const [rc, rct] = await Promise.all([api.get('/clientes/' + v + '/cnpjs'), api.get('/clientes/' + v + '/contatos')]);
            setCnpjs(rc.data || []); setContatos(rct.data || []);
          }}>
            {clientes.map(c => <Option key={c.id} value={c.id}>{c.razao_social || c.nome}</Option>)}
          </Select>
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="cnpj_id" label="CNPJ Faturamento">
              <Select allowClear>{cnpjs.map(c => <Option key={c.id} value={c.id}>{c.cnpj} — {c.razao_social || c.cidade}</Option>)}</Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="contato_id" label="Contato">
              <Select allowClear>{contatos.map(c => <Option key={c.id} value={c.id}>{c.nome}</Option>)}</Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="descricao_equipamento" label="Descrição do Equipamento" rules={[{ required: true }]}>
          <Input placeholder="Ex: Válvula de segurança DN50, Manômetro 0-10 bar..." />
        </Form.Item>
        <Form.Item name="numero_serie" label="Número de Série">
          <Input placeholder="Número de série do equipamento" />
        </Form.Item>
        <Form.Item name="ncm" label="NCM do Equipamento" rules={[{ required: true, message: 'Informe o NCM' }]}>
          <Input placeholder="Ex: 84799090 (equipamentos), 90262000 (manômetros), 84818010 (válvulas)" />
        </Form.Item>
        <Form.Item name="valor" label="Valor (opcional — alguns estados exigem mínimo de R$ 0,01)">
          <InputNumber style={{ width: '100%' }} min={0} precision={2} decimalSeparator="," prefix="R$" placeholder="0,00" />
        </Form.Item>
        <Form.Item shouldUpdate={(prev, curr) => prev.tipo !== curr.tipo} noStyle>
          {({ getFieldValue }) => getFieldValue('tipo') === 'saida' && (
            <Form.Item name="nfe_entrada_id" label="NF-e de Remessa Original (opcional)">
              <Select allowClear placeholder="Selecione a NF-e de entrada correspondente">
                {remessas.filter(r => r.tipo === 'entrada').map(r => <Option key={r.id} value={r.id}>NF {r.numero} — {r.razao_social} — {r.descricao_equipamento}</Option>)}
              </Select>
            </Form.Item>
          )}
        </Form.Item>
        <div style={{ padding: 12, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, marginBottom: 12 }}>
          <strong>⚠️ Atenção:</strong> NF-e emitida em ambiente de <strong>homologação</strong> (sem valor fiscal).
        </div>
        <Button type="primary" htmlType="submit" loading={emitindo} block>Emitir NF-e</Button>
      </Form>
    </Modal>
    </>
  );
}


export default function Fiscal() {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Fiscal</h2>
        <p style={{ color: '#888', margin: 0 }}>Gestão de notas fiscais de serviço</p>
      </div>
      <Tabs defaultActiveKey="emitidas" items={[
        { key: 'emitidas', label: <span><FileProtectOutlined /> NFS-e Emitidas</span>, children: <NFSeEmitidas /> },
        { key: 'recebidas', label: <span><FileProtectOutlined /> NFS-e Recebidas</span>, children: <NFSeRecebidas /> },
        { key: 'nfe', label: <span><FileProtectOutlined /> NF-e Emitidas</span>, children: <NFEEmitidas /> },
        { key: 'nfe_recebidas', label: <span><FileProtectOutlined /> NF-e Recebidas</span>, children: <NFERecebidas /> },
        { key: 'remessa', label: <span><FileProtectOutlined /> Remessa/Devolução</span>, children: <NFERemessa /> },
      ]} />
    </div>
  );
}
